"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  buildMarketingFeatureComparisonRows,
  MARKETING_TIER_CARD_FEATURES,
  type MarketingComparisonCell,
} from "@/lib/plan-features-public";
import { PlanPriceDisplay } from "@/components/pricing/plan-price-display";
import type { TierPriceDisplay, PlatformPricingSaleConfig } from "@/lib/platform-pricing-sale";
import { ANNUAL_MONTHS_BILLED, ANNUAL_MONTHS_DELIVERED } from "@/lib/pricing-public";
import { PLATFORM_TRIAL_DAYS } from "@/lib/billing-access";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type PricingClientProps = {
  saleConfig: PlatformPricingSaleConfig;
  /**
   * Monthly list/sale prices for each tier. Computed on the server using
   * `buildTierPriceDisplay` so server-driven sales (owner ops panel) flow
   * through into the marketing page without a client refetch.
   */
  monthly: { starter: TierPriceDisplay; solo: TierPriceDisplay; pro: TierPriceDisplay; agency: TierPriceDisplay };
  /**
   * Annual prices: total per year (billed once) + the per-month-equivalent for
   * the "$X.XX/mo billed annually" subline. Sales are always applied to the
   * annual list price the same way the server applies them to monthly.
   */
  annual: {
    starter: { total: number; perMonth: number };
    solo: { total: number; perMonth: number };
    pro: { total: number; perMonth: number };
    agency: { total: number; perMonth: number };
  };
};

function CmpCell({ cell }: { cell: MarketingComparisonCell }) {
  if (cell.kind === "text") {
    return <span className="text-slate-600">{cell.value}</span>;
  }
  return (
    <span className="inline-flex justify-center" title="Included">
      <Check className="h-5 w-5 text-emerald-600" strokeWidth={2.5} aria-label="Included" />
    </span>
  );
}

type Period = "monthly" | "annual";

export function PricingClient({ saleConfig, monthly, annual }: PricingClientProps) {
  const [period, setPeriod] = useState<Period>("monthly");
  const saleTitle = saleConfig.enabled ? saleConfig.title : null;

  const tiers = useMemo(
    () => [
      {
        key: "starter" as const,
        name: "Starter",
        desc: "For getting started — unlimited quotes + AI drafting (3/day); quoting-only, no online payments",
        highlight: false,
        features: [...MARKETING_TIER_CARD_FEATURES.starter],
      },
      {
        key: "solo" as const,
        name: "Solo",
        desc: "For one-person shops getting organized",
        highlight: false,
        features: [...MARKETING_TIER_CARD_FEATURES.solo],
      },
      {
        key: "pro" as const,
        name: "Pro",
        desc: "For small teams that need AI + analytics",
        highlight: true,
        features: [...MARKETING_TIER_CARD_FEATURES.pro],
      },
      {
        key: "agency" as const,
        name: "Agency",
        desc: "For bigger teams with custom branding",
        highlight: false,
        features: [...MARKETING_TIER_CARD_FEATURES.agency],
      },
    ],
    [],
  );

  const isAnnual = period === "annual";

  /**
   * Annual savings shown on the toggle pill. Derived from the public
   * pricing constants so the badge stays in sync if we ever change the
   * months-billed / months-delivered ratio (today: bill 10, deliver 12
   * → 16.67% → "17% OFF").
   */
  const annualPercentOff = Math.round(
    ((ANNUAL_MONTHS_DELIVERED - ANNUAL_MONTHS_BILLED) / ANNUAL_MONTHS_DELIVERED) * 100
  );

  const comparisonRows = useMemo(
    () => buildMarketingFeatureComparisonRows({ isAnnual, monthly, annual }),
    [isAnnual, monthly, annual],
  );

  return (
    <section className="border-b border-slate-200/80 bg-white py-12 sm:py-14" id="pricing">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Pricing</h2>
          <p className="mt-4 text-base text-slate-600 sm:text-lg">
            Simple plans. Unlimited quotes on every tier. Pick what fits your crew. Every plan starts with a{" "}
            {PLATFORM_TRIAL_DAYS}-day free trial — no card required. Annual saves two months.
          </p>
          {saleConfig.enabled && saleConfig.title ? (
            <p className="mt-3 inline-flex rounded-full bg-rose-100 px-3 py-1 text-sm font-semibold text-rose-800">
              {saleConfig.title}
            </p>
          ) : null}
          <div className="mt-6 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => setPeriod("monthly")}
              aria-pressed={!isAnnual}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition",
                !isAnnual ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900",
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setPeriod("annual")}
              aria-pressed={isAnnual}
              className={cn(
                "inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition",
                isAnnual ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900",
              )}
            >
              Annual
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                {annualPercentOff}% off
              </span>
            </button>
          </div>
        </div>
        <div className="mx-auto mt-10 grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {tiers.map((t) => {
            const monthlyDisplay = monthly[t.key];
            const annualForTier = annual[t.key];
            return (
              <div
                key={t.name}
                className={`flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition-shadow ${
                  t.highlight
                    ? "border-primary/30 shadow-lg shadow-slate-900/5 ring-1 ring-primary/20 lg:scale-[1.02] lg:shadow-xl"
                    : "border-slate-200 hover:shadow-md"
                }`}
              >
                {t.highlight && (
                  <span className="mb-4 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                    Most popular
                  </span>
                )}
                {!t.highlight && <span className="mb-4 block h-7" aria-hidden />}
                <h3 className="text-lg font-semibold text-slate-900">{t.name}</h3>
                <p className="mt-2 min-h-[2.75rem] text-sm text-slate-600">{t.desc}</p>
                <div className="mt-6">
                  {isAnnual ? (
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold tracking-tight text-slate-900">
                          ${annualForTier.perMonth.toFixed(2)}
                        </span>
                        <span className="text-base font-medium text-slate-500">/mo</span>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        Billed ${annualForTier.total}/yr ({ANNUAL_MONTHS_BILLED} months billed,{" "}
                        {ANNUAL_MONTHS_DELIVERED} months of access)
                      </p>
                    </div>
                  ) : (
                    <PlanPriceDisplay
                      display={monthlyDisplay}
                      variant="marketing"
                      saleTitle={
                        monthlyDisplay.saleActive
                          ? saleTitle ||
                            (monthlyDisplay.percentOff != null ? `${monthlyDisplay.percentOff}% off` : null)
                          : null
                      }
                    />
                  )}
                </div>
                <ul className="mt-8 flex-1 space-y-3 border-t border-slate-100 pt-8">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Button
                    asChild
                    className="h-11 w-full text-[15px] font-semibold"
                    variant={t.highlight ? "default" : "outline"}
                    size="lg"
                  >
                    <Link href={`/signup?plan=${t.key}&billing=${period}`}>Get started</Link>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mx-auto mt-14 max-w-6xl">
          <h3 className="text-xl font-semibold text-slate-900">Feature comparison</h3>
          <p className="mt-2 text-sm text-slate-600">
            All plans include the same core quoting workflow. Higher tiers add AI tools, more team members, calendar
            seats, analytics, and custom branding.
          </p>
          <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-slate-700">Feature</th>
                  <th className="px-4 py-3 text-center font-semibold text-slate-700">Starter</th>
                  <th className="px-4 py-3 text-center font-semibold text-slate-700">Solo</th>
                  <th className="px-4 py-3 text-center font-semibold text-slate-700">Pro</th>
                  <th className="px-4 py-3 text-center font-semibold text-slate-700">Agency</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.feature} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-800">{row.feature}</td>
                    <td className="px-4 py-3 text-center">
                      <CmpCell cell={row.starter} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CmpCell cell={row.solo} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CmpCell cell={row.pro} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CmpCell cell={row.agency} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
